import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  ClipboardList,
  FileCheck,
  FileText,
  PackageCheck,
  Receipt,
} from "lucide-react";

import { procurementAPI } from "../../services/businessErpService";
import OpenWorkPanel from "../../components/OpenWorkPanel";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { EmptyState, PageHeader } from "../../components/erp";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ProcurementDashboard() {
  const [openWork, setOpenWork] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [openWorkRes, timelineRes] = await Promise.all([
        procurementAPI.openWork({ limit: 4 }),
        procurementAPI.timeline({ limit: 8 }),
      ]);
      setOpenWork(openWorkRes?.data || null);
      setTimeline(Array.isArray(timelineRes?.data) ? timelineRes.data : []);
    } catch {
      setOpenWork(null);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = openWork?.summary || {};
  const openWorkItems = [
    Number(summary.submittedPrCount || 0) > 0
      ? {
          id: "procurement-prs",
          title: "Submitted requisitions awaiting action",
          description: "Purchase requisitions still need approval or RFQ follow-through.",
          count: Number(summary.submittedPrCount || 0),
          tone: "amber",
          href: "/business/procurement/open-work?lane=requisitions",
          actionLabel: "Open queue",
        }
      : null,
    Number(summary.openRfqCount || 0) > 0
      ? {
          id: "procurement-rfqs",
          title: "RFQs still open",
          description: "Supplier quote collection or award follow-through is still in progress.",
          count: Number(summary.openRfqCount || 0),
          tone: "blue",
          href: "/business/procurement/open-work?lane=rfqs",
          actionLabel: "Open queue",
        }
      : null,
    Number(summary.pendingFinancePoCount || 0) > 0
      ? {
          id: "procurement-pos",
          title: "Purchase orders pending finance",
          description: "Approved sourcing is blocked until finance review is completed.",
          count: Number(summary.pendingFinancePoCount || 0),
          tone: "rose",
          href: "/business/finance/approvals",
          actionLabel: "Open approvals",
        }
      : null,
    Number(summary.pendingReceiptCount || 0) > 0
      ? {
          id: "procurement-receipts",
          title: "Receipts still open",
          description: "Delivered goods still need receipt posting and inventory confirmation.",
          count: Number(summary.pendingReceiptCount || 0),
          tone: "amber",
          href: "/business/procurement/open-work?lane=pendingReceipts",
          actionLabel: "Open queue",
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Procurement"
        title="Procurement Dashboard"
        subtitle="Use procurement as a command center for requisitions, RFQs, supplier ordering, delivery follow-through, and receipt readiness."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <StatCard title="Submitted PRs" value={loading ? "—" : Number(summary.submittedPrCount || 0)} icon={ClipboardList} />
        <StatCard title="Approved PRs" value={loading ? "—" : Number(summary.approvedPrCount || 0)} icon={ClipboardList} />
        <StatCard title="Open RFQs" value={loading ? "—" : Number(summary.openRfqCount || 0)} icon={FileText} />
        <StatCard title="Pending Finance POs" value={loading ? "—" : Number(summary.pendingFinancePoCount || 0)} icon={FileCheck} accent />
        <StatCard title="Open Deliveries" value={loading ? "—" : Number(summary.openDeliveryCount || 0)} icon={PackageCheck} />
        <StatCard title="Pending Receipts" value={loading ? "—" : Number(summary.pendingReceiptCount || 0)} icon={Receipt} accent />
      </div>

      <OpenWorkPanel
        title="Procurement Open Work Snapshot"
        subtitle="The active procurement queue that still needs approval, sourcing, supplier, finance, or receipt follow-through."
        items={loading ? [] : openWorkItems}
        emptyTitle="Procurement queue is under control"
        emptyDescription="No requisition, RFQ, PO, or receipt blockers are currently open."
      />

      <Panel
          title="Recent Procurement Activity"
          subtitle="Latest recorded procurement events"
          right={
            <Link
              to="/business/procurement/timeline"
              className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100"
            >
              Open timeline <ArrowRight className="h-4 w-4" />
            </Link>
          }
        >
          {loading ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading activity…</div>
          ) : !timeline.length ? (
            <EmptyState icon={Activity} title="No procurement activity yet" description="Procurement timeline events will appear here as sourcing work progresses." />
          ) : (
            <div className="space-y-3">
              {timeline.map((item) => (
                <div key={item.id} className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900/40">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {item.description || item.action_type || "Procurement event"}
                      </div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {item.actor_name || "System"}
                      </div>
                    </div>
                    <div className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">
                      {formatDateTime(item.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </Panel>
    </div>
  );
}
